import {
  ArrowRight,
  Check,
  CircleDollarSign,
  ClipboardCheck,
  Clock3,
  CreditCard,
  Mailbox,
  MapPin,
  Radar,
  ScanLine,
  ShieldCheck,
} from 'lucide-react'
import { Link } from 'react-router-dom'
import { useApp } from '../app-context'
import { Logo } from '../components/Logo'
import { SiteFooter } from '../components/SiteFooter'

const promises = [
  {
    Icon: ClipboardCheck,
    title: 'Customs declaration',
    copy: 'Choose a customs declaration from our fine-tuned presets, or declare it yourself.',
  },
  {
    Icon: CircleDollarSign,
    title: 'The best rates',
    copy: 'We compare shipping carriers to find the best rate and minimize your cost.',
  },
  {
    Icon: Clock3,
    title: '24-hour forwarding',
    copy: 'We ship your package within 24 hours of receipt.',
  },
  {
    Icon: Radar,
    title: 'Tracking on every shipment',
    copy: 'Every shipment includes tracking, so you always know where it is.',
  },
]

export function Landing() {
  const { actor } = useApp()

  return (
    <div className="landing">
      <header className="public-nav">
        <Logo />
        <nav>
          <a href="#route">Our promise</a>
          <a href="#support">What we handle</a>
        </nav>
        <div className="public-nav__actions">
          {actor ? (
            <Link className="button button--dark" to="/dashboard">
              Dashboard <ArrowRight size={16} />
            </Link>
          ) : (
            <>
              <Link className="text-link" to="/login">Sign in</Link>
              <Link className="button button--dark" to="/register">
                Create account <ArrowRight size={16} />
              </Link>
            </>
          )}
        </div>
      </header>

      <main>
        <section className="hero">
          <div className="hero__copy">
            <div className="eyebrow hero__service-label"><span aria-hidden="true" />U.S. package forwarding</div>
            <h1>From in-person U.S cosmetic brands<br /><em>to your doorstep</em></h1>
            <p>Buy directly from your U.S. cosmetic brand. We receive your package and forward it to your door.</p>
            <p className="hero__service-note">Your payment to RX Routing covers forwarding only. Products are purchased separately.</p>
            <div className="hero__actions">
              <Link className="button button--primary button--large" to="/register">
                Get a forwarding quote <ArrowRight size={18} />
              </Link>
              <span>No subscription. Pay per forward.</span>
            </div>
          </div>
          <RouteCard />
        </section>

        <section className="proof-strip">
          <span><ShieldCheck size={18} />Secure one-time payment</span>
          <span><ScanLine size={18} />Warehouse-verified package</span>
          <span><MapPin size={18} />Tracked international delivery</span>
        </section>

        <section id="route" className="steps-section promise-section">
          <div className="section-heading section-heading--promise">
            <h2>Our promise to you</h2>
          </div>
          <div className="steps-grid promise-grid">
            {promises.map(({ Icon, title, copy }) => (
              <article key={title}>
                <div className="promise-title"><Icon size={24} /><h3>{title}</h3></div>
                <p>{copy}</p>
              </article>
            ))}
          </div>
        </section>

        <section id="support" className="scope-section">
          <div>
            <h2>Purposefully narrow.</h2>
            <p>We currently route two supported product formats. Narrow scope lets the warehouse keep intake and customs handling consistent.</p>
          </div>
          <div className="scope-list">
            <span><Check />Oral tablets/capsules</span>
            <span><Check />Topical serum</span>
            <small>Destination eligibility is confirmed before forwarding.</small>
          </div>
        </section>
      </main>

      <SiteFooter />
    </div>
  )
}

function RouteCard() {
  return (
    <div className="route-card" aria-label="Example cosmetic-brand forwarding route">
      <div className="airmail-edge" />
      <div className="route-card__head"><span>ROUTE ESTIMATE</span><b>EXAMPLE PACKAGE</b></div>
      <div className="route-stop route-stop--active">
        <i><MapPin size={17} /></i>
        <div><small>STARTS AT</small><b>U.S cosmetic brand</b></div>
      </div>
      <div className="route-line"><span /><span /><span /></div>
      <div className="route-stop">
        <i><CreditCard size={17} /></i>
        <div><b>Enter final address</b><span>Choose and pay for forwarding.</span></div>
      </div>
      <div className="route-line route-line--muted"><span /><span /><span /></div>
      <div className="route-stop route-stop--muted">
        <i><Mailbox size={17} /></i>
        <div><b>Get your forwarding address</b><span>After payment, copy your U.S. forwarding address and one-order RXID from your order page. Use every line at the brand’s checkout.</span></div>
      </div>
      <div className="route-card__foot"><span>PACKAGE STATE</span><b><i /> PENDING SHIPMENT</b></div>
    </div>
  )
}
